<template>
  <div class="relative" ref="wrapperRef">
    <!-- Trigger button -->
    <button
      type="button"
      @click.stop="toggleDropdown"
      :class="[
        'w-full flex items-center justify-center gap-0.5 rounded-lg border py-1 px-1 text-xs font-medium text-white outline-none transition-colors hover:bg-white/30 focus:border-white/60 sm:text-sm',
        hasError ? 'border-red-400 bg-white/20' : 'border-white/30 bg-white/20',
      ]"
      :aria-label="'Currency: ' + modelValue"
      :aria-expanded="isOpen"
    >
      <span>{{ modelValue }}</span>
      <svg class="h-2.5 w-2.5 flex-shrink-0 text-white/70 ml-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
      </svg>
    </button>

    <!-- Dropdown panel (teleported to body to escape stacking contexts) -->
    <Teleport to="body">
      <!-- Backdrop -->
      <div
        v-if="isOpen"
        class="fixed inset-0 z-40"
        @click.stop="closeDropdown"
      ></div>

      <!-- Panel -->
      <div
        v-if="isOpen"
        class="fixed z-50 w-28 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
        :style="panelStyle"
        @click.stop
      >
        <div class="overflow-y-auto max-h-56" ref="panelScrollRef">
          <!-- Popular section -->
          <div class="px-2.5 pt-2 pb-0.5 sticky top-0 bg-white border-b border-gray-50">
            <p class="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Popular</p>
          </div>
          <div
            v-for="c in popularCurrenciesFiltered"
            :key="'pop-' + c"
            @click.stop="select(c)"
            class="px-3 py-1 text-xs cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
            :class="modelValue === c ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'"
          >
            {{ c }}
            <svg v-if="modelValue === c" class="h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
            </svg>
          </div>

          <!-- Divider -->
          <div class="border-t border-gray-100 mx-0 my-0.5"></div>

          <!-- All currencies section -->
          <div class="px-2.5 pt-1 pb-0.5 sticky top-0 bg-white border-b border-gray-50">
            <p class="text-[9px] font-bold text-gray-400 uppercase tracking-wider">All currencies</p>
          </div>
          <div
            v-for="c in currencies"
            :key="'all-' + c"
            @click.stop="select(c)"
            class="px-3 py-1 text-xs cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
            :class="modelValue === c ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'"
          >
            {{ c }}
            <svg v-if="modelValue === c" class="h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: 'USD' },
  currencies: { type: Array, required: true },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change']);

const POPULAR = [
  'EUR', 'GBP', 'USD', 'AUD', 'CAD', 'CHF', 'JPY',
  'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'NZD', 'INR',
  'SGD', 'HKD', 'AED', 'THB', 'TRY', 'ZAR', 'HUF',
];

const popularCurrenciesFiltered = computed(() =>
  POPULAR.filter(c => props.currencies.includes(c))
);

const isOpen = ref(false);
const wrapperRef = ref(null);
const panelScrollRef = ref(null);
const panelStyle = ref({});

const computePanelPosition = () => {
  if (!wrapperRef.value) return;
  const rect = wrapperRef.value.getBoundingClientRect();
  const panelH = 240; // approx panel height
  const spaceBelow = window.innerHeight - rect.bottom;
  const right = Math.max(0, window.innerWidth - rect.right);

  if (spaceBelow < panelH && rect.top > panelH) {
    panelStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      right: `${right}px`,
    };
  } else {
    panelStyle.value = {
      top: `${rect.bottom + 4}px`,
      right: `${right}px`,
    };
  }
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const openDropdown = async () => {
  computePanelPosition();
  isOpen.value = true;
  await nextTick();
  // Scroll selected item into view within the panel
  if (panelScrollRef.value) {
    const selected = panelScrollRef.value.querySelector('.bg-blue-50');
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }
};

const closeDropdown = () => {
  isOpen.value = false;
};

const select = (currency) => {
  emit('update:modelValue', currency);
  emit('change', currency);
  closeDropdown();
};

const handleKeydown = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>
